/**
 * La pestaña Corrección del panel de Word (Fase 20, paso 5 · 10-oct-2026).
 *
 * Cuatro pasadas del corrector sobre la novela construida —naturalidad, estilo, gramática y ortotipografía—, un
 * botón cada una. Cada pasada deja al lado una versión nueva con sus cambios marcados y la abre en Word; el
 * autor decide ahí, con Revisar, guarda y pasa la siguiente desde el panel de esa misma ventana.
 *
 * Nació en la pantalla grande y Rais la trajo aquí: corregir es trabajar sobre el documento, y eso se hace en
 * Word. Vale con el manuscrito abierto o con la novela (o cualquiera de sus versiones) abierta.
 *
 * A Word solo le pregunta dónde está guardado el documento, como la pestaña Guion. Gasta SOLO cuando el autor
 * pulsa y confirma, y solo pregunta sola al motor mientras hay una pasada en marcha.
 */
import { useCallback, useEffect, useState } from "react";
import AceptarPendientes from "./AceptarPendientes";
import { getDocumentUrl } from "./office";
import { COMPANION } from "./companion";
import { rutaLocal, carpetaDe } from "./guionLogica";
import { porQueNo, fraseDeLoQueViaja, fraseDeHecha, fraseDeAvance, avanceDe, fraseDeResultado, fraseDeSueltas, fraseDeAbierta, dudasDeAhora, quedaPorLeer, enMarcha, noticiaDe, dinero,
  puedeAbrirse, fraseDeNoAbierta, NO_ABIERTA_LA_DE_LA_LINEA, type Correccion as Datos, type SinCorreccion, type Pasada } from "./correccionLogica";

export type LibroAbierto = { hay: true; dir: string; doc: string } | { hay: false; motivo: "sin_guardar" | "nube" };
export type PuenteDeLaCorreccion = {
  /** Dónde está guardado el documento abierto. Es lo único que se le pregunta a Word. */
  libro: () => Promise<LibroAbierto>;
  estado: (dir: string, doc: string) => Promise<Datos | SinCorreccion>;
  /** Lanza una pasada. Es lo único de esta pestaña que gasta. */
  corregir: (dir: string, doc: string, pasada: string) => Promise<unknown>;
  /** Lee lo que el autor decidió en Word con la última pasada. No gasta. */
  leer: (dir: string, doc: string) => Promise<Datos | SinCorreccion>;
  /** Abre en Word la última versión o, con `k`, la que dejó esa pasada. Solo viaja el número: el nombre lo pone el motor. */
  abrir: (dir: string, doc: string, k?: number) => Promise<{ abierto: boolean; fichero: string }>;
};

async function leerRespuesta(r: Response) {
  const d = await r.json().catch(() => null);
  if (!r.ok || !d) throw new Error(d?.error?.message ?? d?.error ?? (r.status === 402 ? "Tu licencia de HumanInk no está activa en este ordenador." : "HumanInk no ha contestado. Comprueba que está abierto en este ordenador."));
  return d;
}
const mandar = async (ruta: string, cuerpo: object) => leerRespuesta(await fetch(`${COMPANION}${ruta}`, {
  method: "POST", headers: { "content-type": "application/json", "x-hi-cliente": "word" }, body: JSON.stringify(cuerpo),
}));
export const puenteDeWord: PuenteDeLaCorreccion = {
  libro: async () => {
    const url = await getDocumentUrl();
    const ruta = rutaLocal(url);
    return ruta ? { hay: true, dir: carpetaDe(ruta), doc: ruta } : { hay: false, motivo: url ? "nube" : "sin_guardar" };
  },
  estado: async (dir, doc) => leerRespuesta(await fetch(`${COMPANION}/guion/correcciones?${new URLSearchParams({ dir, doc })}`)),
  corregir: (dir, doc, pasada) => mandar("/guion/corregir", { dir, doc, pasada }),
  leer: (dir, doc) => mandar("/guion/correcciones/leer", { dir, doc }),
  abrir: (dir, doc, k) => mandar("/guion/correcciones/abrir", k == null ? { dir, doc } : { dir, doc, k }),
};

/** Cada cuánto se pregunta por la pasada EN MARCHA. Sin pasada en marcha no se pregunta nada. */
const CADA = 2500;

// Los estilos son de esta pestaña y cuelgan de la paleta del panel (`tema.ts`): se ven en claro y en oscuro.
export const CSS_DE_LA_CORRECCION = `
.hc-raiz { font-size:13px; line-height:1.45; color:var(--hi-tinta); }
.hc-raiz h1 { font-size:16px; margin:0 0 2px; font-weight:800; text-wrap:balance; }
.hc-tenue { color:var(--hi-tenue); font-size:12px; overflow-wrap:anywhere; }
.hc-botones { display:flex; flex-wrap:wrap; gap:6px; margin:8px 0 2px; }
.hc-boton { font:inherit; font-size:12px; font-weight:700; padding:7px 12px; border-radius:8px; border:1px solid var(--hi-raya-2); background:var(--hi-papel); color:var(--hi-suave); cursor:pointer; }
.hc-boton:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hc-boton:disabled { opacity:.5; cursor:not-allowed; }
.hc-principal { background:var(--hi-humano); border-color:var(--hi-humano); color:var(--hi-sobre-acento); }
.hc-carta { background:var(--hi-papel); border:1px solid var(--hi-raya); border-radius:10px; padding:12px; margin-top:10px; }
.hc-nota { background:var(--hi-papel); border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; margin-top:10px; overflow-wrap:anywhere; }
.hc-nota[data-tono="bien"] { border-color:var(--hi-bien-raya); }
.hc-nota[data-tono="mal"] { border-color:var(--hi-mal-raya); }
.hc-nota p { margin:0 0 6px; } .hc-nota p:last-child { margin-bottom:0; }
.hc-avance { width:100%; height:8px; margin:6px 0 4px; accent-color:var(--hi-oficio); }
.hc-dudas { margin:4px 0 0; padding-left:18px; } .hc-dudas li { margin-bottom:4px; }
.hc-pasadas { margin:10px 0 0; padding:0; list-style:none; display:grid; gap:8px; }
.hc-pasada { background:var(--hi-papel); border:1px solid var(--hi-raya); border-radius:10px; padding:12px; }
.hc-pasada[data-toca="si"] { border-color:var(--hi-oficio); }
.hc-pasada h2 { font-size:15px; margin:0 0 4px; font-weight:800; }
.hc-pasada h2 small { font-size:11px; font-weight:400; color:var(--hi-tenue); margin-left:8px; }
.hc-pasada p { margin:0; }
.hc-pasada ul { margin:6px 0 0; padding:0; list-style:none; font-size:12px; color:var(--hi-tenue); }
.hc-pasada ul li { padding:2px 0; overflow-wrap:anywhere; }
.hc-pasada .hc-boton { margin-top:8px; }
.hc-pasada ul .hc-chico { margin:0 0 0 6px; padding:2px 8px; font-size:11px; font-weight:600; vertical-align:baseline; }
.hc-pasada ul .hc-nota { margin-top:4px; padding:6px 10px; color:var(--hi-tinta); }
.hc-confirma { border-top:1px solid var(--hi-raya); margin-top:10px; padding-top:8px; }
`;

const SIN_DOCUMENTO = {
  sin_guardar: "Guarda el documento en tu ordenador y vuelve a esta pestaña: así sé de qué libro es.",
  nube: "Este documento está en la nube. Guárdalo en una carpeta de tu ordenador para poder corregir la novela.",
} as const;

export default function Correccion({ puente = puenteDeWord }: { puente?: PuenteDeLaCorreccion }) {
  const [libro, setLibro] = useState<LibroAbierto | null>(null);
  const [datos, setDatos] = useState<Datos | SinCorreccion | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [pedida, setPedida] = useState<string | null>(null);          // la pasada que espera el «sí»
  const [ocupado, setOcupado] = useState(false);
  const [seguida, setSeguida] = useState<string | null>(null);        // la pasada que esta pestaña ha visto trabajar
  const [deLaLinea, setDeLaLinea] = useState<{ k: number; texto: string } | null>(null);   // lo que contestó abrir UNA versión

  const cargar = useCallback(async () => {
    try {
      const l = await puente.libro();
      setLibro(l);
      if (!l.hay) return;
      setDatos(await puente.estado(l.dir, l.doc));
      setFallo(null);
    } catch (e) { setFallo(e instanceof Error ? e.message : String(e)); }
  }, [puente]);

  useEffect(() => {
    void cargar();
    const alVolver = () => void cargar();
    window.addEventListener("focus", alVolver);
    return () => window.removeEventListener("focus", alVolver);
  }, [cargar]);

  // Solo mientras hay una pasada en marcha: se pregunta cómo va, y se deja de preguntar en cuanto acaba.
  const trabajando = enMarcha(datos?.trabajo);
  useEffect(() => {
    if (!trabajando) return;
    setSeguida(datos?.trabajo?.id ?? null);
    const reloj = setTimeout(() => void cargar(), CADA);
    return () => clearTimeout(reloj);
  }, [trabajando, datos, cargar]);

  /** `alFallar`: dónde se dice lo que falle. Por omisión, arriba; lo de una versión, debajo de su línea. */
  const hacer = async (que: () => Promise<unknown>, alFallar: (texto: string) => void = setFallo) => {
    if (ocupado) return;
    setOcupado(true); setFallo(null); setDeLaLinea(null);
    try { await que(); await cargar(); }
    catch (e) { alFallar(e instanceof Error ? e.message : String(e)); }
    finally { setOcupado(false); setPedida(null); }
  };

  const marco = (dentro: React.ReactNode) => <div className="hc-raiz"><style>{CSS_DE_LA_CORRECCION}</style>{dentro}</div>;
  if (libro && !libro.hay) return marco(<p className="hc-carta">{SIN_DOCUMENTO[libro.motivo]}</p>);
  if (!datos || !libro) return marco(fallo ? <div className="hc-nota" data-tono="mal" role="alert"><p>{fallo}</p></div> : <p className="hc-tenue">Mirando la novela…</p>);
  const { dir, doc } = libro;

  if (!datos.hay) {
    return marco(<>
      <h1>Corregir la novela</h1>
      <p className="hc-carta">
        {datos.motivo === "sin_guion"
          ? "Este libro todavía no tiene guion. Tráelo desde la pestaña Guion."
          : datos.motivo === "varios_libros"
          ? "En la carpeta de este documento hay más de un libro y no sé de cuál es. Abre en Word el manuscrito del libro que quieras corregir y vuelve a esta pestaña."
          : "Todavía no has construido la novela. Se construye en la pestaña Guion, con las escenas que hayas cerrado; cuando esté, aquí podrás pasarle el corrector."}
      </p>
      <div className="hc-botones"><button className="hc-boton" onClick={() => void cargar()}>Actualizar</button></div>
    </>);
  }

  const freno = porQueNo(datos);
  const t = datos.trabajo;
  const dudas = dudasDeAhora(datos);
  const noticia = noticiaDe(datos, seguida);
  // Si la noticia ya dice «decide en Word», el freno diría lo mismo debajo.
  const frenoRepetido = noticia === "hecha" && (datos.actual.marcas ?? 0) > 0;
  const abrirla = () => void hacer(async () => {
    const r = await puente.abrir(dir, doc);
    if (!r.abierto) throw new Error(fraseDeNoAbierta(r.fichero));
  });
  // La versión que dejó UNA pasada (encargo 0002). Lo que conteste va debajo de ESA línea, que es donde está mirando el
  // autor (lección del 9-oct); y se mira otra vez: si su Word se ha ido, la línea lo dice y pierde el botón.
  const abrirLaDe = (k: number) => void hacer(async () => {
    const r = await puente.abrir(dir, doc, k);
    if (!r.abierto) throw new Error(NO_ABIERTA_LA_DE_LA_LINEA);
  }, (texto) => { setDeLaLinea({ k, texto }); void cargar(); });
  return marco(<>
    <h1>Corregir «{datos.titulo ?? "la novela"}»</h1>
    <div className="hc-tenue">
      {datos.actual.es_la_construida ? "La novela, tal como la construiste" : "Última versión"}: «{datos.actual.fichero}»
      {datos.gasto ? ` · gastado en corregirla: ${dinero(datos.gasto)}` : ""}
    </div>
    <div className="hc-botones">
      <button className="hc-boton" disabled={ocupado || !datos.actual.existe} onClick={abrirla}>Abrirla en Word</button>
      <button className="hc-boton" onClick={() => void cargar()}>Actualizar</button>
    </div>

    {fallo && <div className="hc-nota" data-tono="mal" role="alert"><p>{fallo}</p></div>}
    {fraseDeAbierta(datos) && <div className="hc-nota"><p>{fraseDeAbierta(datos)}</p></div>}

    {t && noticia === "en_marcha" && (
      <div className="hc-nota" role="status">
        <p><b>{t.titulo}</b>, en marcha. {fraseDeAvance(t)}</p>
        <progress className="hc-avance" value={avanceDe(t)} max={1} aria-label="Avance de la pasada" />
        <p className="hc-tenue">Puedes seguir trabajando o cerrar el panel: la pasada sigue, y la versión nueva se abrirá en Word cuando esté.</p>
      </div>
    )}
    {t && noticia === "hecha" && t.resultado && <div className="hc-nota" data-tono="bien" role="status"><p><b>{t.titulo}</b>, hecha. {fraseDeResultado(t.resultado)}</p></div>}
    {t && noticia === "fallida" && (
      <div className="hc-nota" data-tono="mal" role="alert"><p><b>{t.titulo}</b> no ha terminado. {t.error ?? "Vuelve a lanzarla."}</p></div>
    )}

    {freno && !enMarcha(t) && !frenoRepetido && <div className="hc-nota"><p>{freno}</p></div>}
    {/* Lo que queda sin decidir, darlo por aceptado (14-oct-2026). Solo si el Word abierto es la versión que lo tiene:
        es en el abierto donde Word los acepta. */}
    {!enMarcha(t) && (datos.actual.marcas ?? 0) > 0 && datos.abierta?.es_la_ultima === true && (
      <AceptarPendientes n={datos.actual.marcas ?? 0} dir={dir} doc={doc} alTerminar={() => void cargar()} />
    )}
    {quedaPorLeer(datos) && (
      <div className="hc-nota">
        <p>«{datos.actual.fichero}» ya no tiene cambios por decidir.</p>
        <button className="hc-boton" disabled={ocupado} onClick={() => void hacer(() => puente.leer(dir, doc))}>Leer lo que decidí</button>
      </div>
    )}
    {fraseDeSueltas(datos) && <div className="hc-nota"><p>{fraseDeSueltas(datos)}</p></div>}
    {dudas.length > 0 && (
      <div className="hc-nota">
        <p><b>Lo que el corrector te deja a ti</b> (no lo ha tocado):</p>
        <ul className="hc-dudas">{dudas.flatMap((d) => d.asuntos.map((a, i) => <li key={`${d.capitulo}-${i}`}><span className="hc-tenue">{d.capitulo} · </span>{a}</li>))}</ul>
      </div>
    )}

    <ol className="hc-pasadas">
      {datos.pasadas.map((p, n) => <FilaDePasada key={p.id} p={p} n={n + 1} toca={datos.siguiente === p.id} freno={freno} ocupado={ocupado} pedida={pedida === p.id}
        queViaja={fraseDeLoQueViaja(datos)} pedir={() => setPedida(p.id)} dejar={() => setPedida(null)}
        confirmar={() => void hacer(() => puente.corregir(dir, doc, p.id))} abrir={abrirLaDe} deLaLinea={deLaLinea} />)}
    </ol>
    <p className="hc-tenue" style={{ marginTop: 10 }}>
      El orden es el recomendado, no obligatorio. Cada pasada escribe una versión nueva junto a la anterior, que no se toca; los cambios van firmados con el nombre de la pasada, para aceptarlos o rechazarlos en bloque desde Revisar.
    </p>
  </>);
}

/**
 * Una pasada: qué hace, lo ya hecho con ella (cada versión que dejó, con su botón para abrirla si sigue en la carpeta),
 * y su botón, que pregunta antes de gastar.
 */
function FilaDePasada({ p, n, toca, freno, ocupado, pedida, queViaja, pedir, dejar, confirmar, abrir, deLaLinea }: {
  p: Pasada; n: number; toca: boolean; freno: string | null; ocupado: boolean; pedida: boolean; queViaja: string; pedir: () => void; dejar: () => void; confirmar: () => void;
  abrir: (k: number) => void; deLaLinea: { k: number; texto: string } | null;
}) {
  const hecha = p.hechas.length > 0;
  return (
    <li className="hc-pasada" data-toca={toca ? "si" : "no"}>
      <h2>{n} · {p.nombre}{toca ? <small>la siguiente</small> : hecha ? <small>✓ hecha</small> : null}</h2>
      <p>{p.hace} <span className="hc-tenue">{p.no_hace}</span></p>
      {hecha && <ul>{p.hechas.map((h) => (
        <li key={h.k}>
          {fraseDeHecha(h)}
          {puedeAbrirse(h) && <button className="hc-boton hc-chico" disabled={ocupado} aria-label={`Abrir «${h.fichero}» en Word`} onClick={() => abrir(h.k)}>Abrir en Word</button>}
          {deLaLinea?.k === h.k && <div className="hc-nota" data-tono="mal" role="alert"><p>{deLaLinea.texto}</p></div>}
        </li>
      ))}</ul>}
      <button className={`hc-boton${toca ? " hc-principal" : ""}`} disabled={!!freno || ocupado || pedida} onClick={pedir}>{hecha ? "Pasarla otra vez" : "Pasarla"}</button>
      {pedida && (
        <div className="hc-confirma">
          {queViaja} La versión que hay no se toca: saldrá otra al lado, con los cambios marcados.
          <div className="hc-botones">
            <button className="hc-boton hc-principal" disabled={ocupado} onClick={confirmar}>Sí, pasar la de {p.nombre.toLowerCase()}</button>
            <button className="hc-boton" disabled={ocupado} onClick={dejar}>No</button>
          </div>
        </div>
      )}
    </li>
  );
}
